import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import {  useLocation, useNavigate } from "react-router-dom"
import { ShimmerThumbnail } from "react-shimmer-effects"
import {
  Badge,
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Table,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { getItemGroupDetails } from "store/actions/itemGroupsAction"
import formatDateTime from "components/Shared/formatDateTime"
import placeholderImage from "assets/images/placeholderImage.png"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"

const ItemGroupDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Item Group Details ")
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const { loading, itemGroupDetails } = useSelector(state => state.ItemGroups)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const menusSubPermissions = getSubPermissions(
      userRole,
      4,
      "menus_1",
      accountType
    )
    const isPermissionView = isPermissionEnabled(
      userRole,
      4,
      "menus_1",
      accountType
    )
    if (
      !menusSubPermissions?.combo_access_view?.isEnabled ||
      !isPermissionView
    ) {
      navigate("/dashboard")
    }
  }, [])

  useEffect(() => {
    getOldValues()
  }, [])

  const getOldValues = () => {
    const payload = {
      id,
    }
    dispatch(getItemGroupDetails(payload))
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        {loading ? (
          <Card>
            <CardBody>
              <Row>
                <Col lg={6}>
                  <ShimmerThumbnail height={430} rounded />
                </Col>
                <Col lg={6}>
                  <ShimmerThumbnail height={430} rounded />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ) : (
          <Row>
            <Col lg={6}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3 d-flex align-items-center">
                    <BackButton /> {t("Item Group Details")}
                  </CardTitle>
                  <div className="table-responsive">
                    {Object.values(itemGroupDetails).length === 0 ? (
                      <p className="text-center">
                        {t("No item group details found")}
                      </p>
                    ) : (
                      <Table className="table-nowrap mb-0">
                        <tbody>
                          <tr>
                            <th scope="row">{t("Id")}</th>
                            <td>{itemGroupDetails._id}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Name(English)")}</th>
                            <td>{itemGroupDetails.name || "-"}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Name(عربي)")}</th>
                            <td>{itemGroupDetails.nameAr || "-"}</td>
                          </tr>

                          <tr>
                            <th scope="row">{t("Minimum")}</th>
                            <td>{itemGroupDetails.minimum ?? "-"}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Maximum")}</th>
                            <td>{itemGroupDetails.maximum ?? "-"}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Quantity")}</th>
                            <td>{itemGroupDetails.quantity ?? "-"}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Status")}</th>
                            <td>
                              {itemGroupDetails.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Active")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Deactive")}
                                </Badge>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Created At")}</th>
                            <td>
                              {formatDateTime(itemGroupDetails.createdAt) ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Updated At")}</th>
                            <td>
                              {formatDateTime(itemGroupDetails.updatedAt) ||
                                "-"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="card_100_24">
                <CardBody style={{ maxHeight: 475, overflowY: "auto" }}>
                  <CardTitle>{t("Items")}</CardTitle>
                  {loading ? null : itemGroupDetails?.items?.length === 0 ? (
                    <p className="text-center my-2">{t("No items found")}</p>
                  ) : (
                    <Table className="table mb-0 capitalize_td">
                      <thead className="">
                        <tr>
                          <th>{t("Image")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Status")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemGroupDetails?.items?.map((item, index) => (
                          <tr
                            key={item._id}
                            className="cursor-pointer td_middle"
                            onClick={() => {
                              navigate(
                                `/menus/items/item-details?id=${item._id}`
                              )
                            }}
                          >
                            <td>
                              <img
                                style={{
                                  maxWidth: 50,
                                  maxHeight: 50,
                                  objectFit: "contain",
                                }}
                                src={item.imageUrl || placeholderImage}
                                alt="item image"
                              />
                            </td>
                            <td>
                              {language === "sa"
                                ? item?.nameAr
                                : item?.name || "-"}
                            </td>
                            <td>
                              {item?.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Active")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Deactive")}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  )
}

export default ItemGroupDetails
